/**
 * GuestChatConfigForm — the admin "Guest access" card at the top of the App
 * Config page (SCRUM-273/274; spec "Admin 'Guest access' form", precedent
 * BudgetConfigForm).
 *
 * Switches are FEATURE FLAGS (codebase rule, admin/config_bounds.py:78;
 * precedent `stt.enabled`): PUT /admin/feature-flags/{key}, which needs
 * ops.feature_flags.manage; the row's rules and description go back
 * unchanged. Numbers are app_config int keys with registered bounds:
 * PUT /admin/config/{key} (ops.app_config.write). The client checks the
 * contract's bounds for a quick answer; the server's registry is
 * authoritative and its 422 is shown in the status line. Both hot-reload —
 * the next guest send uses them. Only CHANGED values are written, so saving
 * the card never rewrites a key an operator set elsewhere.
 *
 * Controller ruling P2 + fix round 2: `status` AND the `useSaveGuestConfig()`
 * mutation itself both live in THIS component, not in the server-data-keyed
 * `GuestAccessFields` below. `GuestAccessFields` is remounted (via its `key`)
 * whenever the server's values change — which is exactly what happens right
 * after a successful save, once the post-save refetch lands.
 *
 * `status` alone was not enough (fix round 1 found this the hard way, via a
 * real e2e run against a real browser and API): the mutation's hook-level
 * `onSettled` AWAITS that refetch before the mutation dispatches "success" to
 * its own observer, and TanStack Query's MutationObserver only delivers a
 * mutate-call's `{ onSuccess, onError }` `if (hasListeners())` — see
 * node_modules/@tanstack/query-core's mutationObserver.js `notify_fn`. If the
 * refetch's data change has ALREADY remounted — and so unsubscribed — a
 * mutation owned by the child by the time dispatch runs, that per-call
 * callback is silently dropped and `setStatus("saved")` never fires, leaving
 * the status stuck on "saving" forever. Owning the mutation here too means
 * the SAME observer survives the remount, so its callbacks always land
 * (test: "reaches 'saved' even when the post-save refetch remounts the
 * fields before the mutation settles" in GuestChatConfigForm.test.tsx).
 *
 * DRAFTED STATE (GUARDRAILS rule 10): no prototype for this admin card; the
 * admin surface's own checkbox + Field anatomy, `--g-*` tokens, a polite
 * status line instead of a toast so the outcome stays next to the button.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useId, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button, Field } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import type { AppConfigItem } from "@/features/admin/app-config/api";
import {
  useConfigList,
  useSaveGuestConfig,
  type GuestConfigChanges,
} from "@/features/admin/app-config/hooks";
import type { AdminFeatureFlag } from "@/features/admin/feature-flags/api";
import { useFlags } from "@/features/admin/feature-flags/hooks";
import { useRequirePermission } from "@/features/admin/hooks/useRequirePermission";
import { ApiError } from "@/lib/api-client";

/** The four switches (feature flags; contract "Switches and settings"). */
export const GUEST_SWITCHES = [
  { key: "guest.enabled", field: "enabled", testId: "guest-access-enabled", fallback: false },
  { key: "guest.soft_mode", field: "softMode", testId: "guest-access-soft-mode", fallback: false },
  { key: "guest.attachments_enabled", field: "attachments", testId: "guest-access-attachments", fallback: false },
  { key: "guest.voice_enabled", field: "voice", testId: "guest-access-voice", fallback: true },
] as const;

/** The eight numbers (app_config ints; contract bounds and defaults). */
export const GUEST_NUMBERS = [
  { key: "guest.max_messages", field: "maxMessages", testId: "guest-access-max-messages", min: 1, max: 1000, fallback: 10 },
  { key: "guest.max_tokens", field: "maxTokens", testId: "guest-access-max-tokens", min: 0, max: 10_000_000, fallback: 0 },
  { key: "guest.soft_reprompt_every", field: "softRepromptEvery", testId: "guest-access-soft-reprompt-every", min: 1, max: 50, fallback: 3 },
  { key: "guest.warn_at_remaining", field: "warnAtRemaining", testId: "guest-access-warn-at-remaining", min: 0, max: 20, fallback: 0 },
  { key: "guest.retention_days", field: "retentionDays", testId: "guest-access-retention-days", min: 1, max: 90, fallback: 7 },
  { key: "guest.max_new_sessions_per_ip_per_day", field: "ipCap", testId: "guest-access-ip-cap", min: 1, max: 100_000, fallback: 30 },
  { key: "guest.daily_token_budget", field: "dailyTokenBudget", testId: "guest-access-daily-token-budget", min: 0, max: 100_000_000, fallback: 0 },
  { key: "guest.stt_daily_seconds", field: "sttDailySeconds", testId: "guest-access-stt-daily-seconds", min: 0, max: 3600, fallback: 120 },
] as const;

const intIn = (min: number, max: number) =>
  z
    .string()
    .refine((value) => /^\d+$/.test(value.trim()) && Number(value) >= min && Number(value) <= max);

export const guestAccessSchema = z.object({
  enabled: z.boolean(),
  softMode: z.boolean(),
  attachments: z.boolean(),
  voice: z.boolean(),
  maxMessages: intIn(1, 1000),
  maxTokens: intIn(0, 10_000_000),
  softRepromptEvery: intIn(1, 50),
  warnAtRemaining: intIn(0, 20),
  retentionDays: intIn(1, 90),
  ipCap: intIn(1, 100_000),
  dailyTokenBudget: intIn(0, 100_000_000),
  sttDailySeconds: intIn(0, 3600),
});
export type GuestAccessValues = z.infer<typeof guestAccessSchema>;

type Status =
  | { state: "idle" }
  | { state: "saving" }
  | { state: "saved" }
  | { state: "error"; message: string };

function initialValues(flags: AdminFeatureFlag[], items: AppConfigItem[]): GuestAccessValues {
  const values: Record<string, boolean | string> = {};
  for (const s of GUEST_SWITCHES) {
    values[s.field] = flags.find((f) => f.key === s.key)?.enabled ?? s.fallback;
  }
  for (const n of GUEST_NUMBERS) {
    values[n.field] = items.find((i) => i.key === n.key)?.value ?? String(n.fallback);
  }
  return values as GuestAccessValues;
}

export function GuestChatConfigForm(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const titleId = useId();
  const canManageFlags = useRequirePermission("ops.feature_flags.manage");
  const canWriteConfig = useRequirePermission("ops.app_config.write");
  const flagsQuery = useFlags({ enabled: canManageFlags });
  const configQuery = useConfigList();
  const flags = flagsQuery.data?.items ?? [];
  const items = configQuery.data?.items ?? [];
  const loading = configQuery.isLoading || (canManageFlags && flagsQuery.isLoading);
  const failed = configQuery.isError || (canManageFlags && flagsQuery.isError);
  // Controller ruling P2 + fix round 2: both owned here (not in the keyed
  // `GuestAccessFields` below) so a post-save refetch — which remounts that
  // child — can neither wipe the "saved" feedback nor drop the mutation's
  // own callbacks. See the file header comment for why the mutation alone
  // moving here was necessary, not just `status`.
  const [status, setStatus] = useState<Status>({ state: "idle" });
  const save = useSaveGuestConfig();

  const errorMessage = (err: unknown): string => {
    if (err instanceof ApiError && err.code === "app_config.value_out_of_range") {
      const { min, max, value } = err.problem;
      if (typeof min === "number" && typeof max === "number" && typeof value === "number") {
        return t("appConfig.error.outOfRange", { min, max, value });
      }
    }
    return t("appConfig.guest.saveError");
  };

  const onSave = (changes: GuestConfigChanges): void => {
    if (changes.flags.length === 0 && changes.numbers.length === 0) {
      setStatus({ state: "saved" });
      return;
    }
    setStatus({ state: "saving" });
    save.mutate(changes, {
      onSuccess: () => setStatus({ state: "saved" }),
      onError: (err) => setStatus({ state: "error", message: errorMessage(err) }),
    });
  };

  return (
    <section
      aria-labelledby={titleId}
      data-testid="guest-access"
      className="flex flex-col gap-4 rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] bg-[var(--g-surface)] p-4 md:p-5"
    >
      <header className="flex flex-col gap-1">
        <h2 id={titleId} className="text-base font-semibold text-[var(--g-primary-ink)]">
          {t("appConfig.guest.title")}
        </h2>
        <p className="text-sm text-[var(--g-secondary)]">{t("appConfig.guest.subtitle")}</p>
      </header>
      {loading ? (
        <p role="status" className="text-sm text-[var(--g-secondary)]">
          {t("loading")}
        </p>
      ) : failed ? (
        <p role="alert" className="text-sm text-[var(--g-error)]">
          {t("appConfig.guest.loadError")}
        </p>
      ) : (
        <GuestAccessFields
          // Remount with fresh defaults whenever the server's values change
          // (after a save the lists refetch).
          key={JSON.stringify(initialValues(flags, items))}
          flags={flags}
          flagsKnown={flagsQuery.data !== undefined}
          items={items}
          canManageFlags={canManageFlags}
          canWriteConfig={canWriteConfig}
          status={status}
          isSaving={save.isPending}
          onSave={onSave}
        />
      )}
    </section>
  );
}

interface GuestAccessFieldsProps {
  flags: AdminFeatureFlag[];
  /** False when flags were not read (no permission) — nothing is "missing" then. */
  flagsKnown: boolean;
  items: AppConfigItem[];
  canManageFlags: boolean;
  canWriteConfig: boolean;
  /** Owned by `GuestChatConfigForm` (controller ruling P2) — survives this
   * component's remount on a server-data change. */
  status: Status;
  /** `useSaveGuestConfig()`'s `isPending`, likewise owned by the parent
   * (fix round 2) — the Button's spinner, nothing more. */
  isSaving: boolean;
  /** Computes nothing itself; the parent owns the mutation (fix round 2) so
   * the SAME observer survives this component's remount. */
  onSave: (changes: GuestConfigChanges) => void;
}

function GuestAccessFields({
  flags,
  flagsKnown,
  items,
  canManageFlags,
  canWriteConfig,
  status,
  isSaving,
  onSave,
}: GuestAccessFieldsProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GuestAccessValues>({
    resolver: zodResolver(guestAccessSchema),
    defaultValues: initialValues(flags, items),
  });

  const onSubmit = handleSubmit((values) => {
    const changes: GuestConfigChanges = { flags: [], numbers: [] };
    if (canManageFlags) {
      for (const s of GUEST_SWITCHES) {
        const flag = flags.find((f) => f.key === s.key);
        if (flag !== undefined && flag.enabled !== values[s.field]) {
          changes.flags.push({ flag, enabled: values[s.field] });
        }
      }
    }
    // Unlike a missing FLAG row above (excluded: PUT /admin/feature-flags/{key}
    // only updates an existing flag, and this form never creates one — its
    // checkbox is disabled instead), a missing NUMBER row IS included here:
    // PUT /admin/config/{key} is an upsert, so saving creates the row at the
    // contract default the admin is already seeing (`item === undefined` on
    // its own makes the condition below true, regardless of whether the
    // value was actually edited).
    if (canWriteConfig) {
      for (const n of GUEST_NUMBERS) {
        const item = items.find((i) => i.key === n.key);
        const value = String(Number(values[n.field].trim()));
        if (item === undefined || item.value !== value) {
          changes.numbers.push({ key: n.key, value, description: item?.description ?? null });
        }
      }
    }
    onSave(changes);
  });

  const statusText =
    status.state === "saving"
      ? t("appConfig.guest.saving")
      : status.state === "saved"
        ? t("appConfig.guest.saved")
        : status.state === "error"
          ? status.message
          : "";

  return (
    <form onSubmit={onSubmit} noValidate data-testid="guest-access-form" className="flex flex-col gap-5">
      <fieldset className="flex flex-col gap-2" disabled={!canManageFlags}>
        <legend className="mb-1 text-sm font-semibold text-foreground">
          {t("appConfig.guest.switches")}
        </legend>
        {GUEST_SWITCHES.map((s) => {
          const missing = flagsKnown && !flags.some((f) => f.key === s.key);
          return (
            <label key={s.key} className="flex min-h-[44px] items-start gap-3 py-1 text-sm">
              <input
                type="checkbox"
                data-testid={s.testId}
                disabled={missing}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--g-primary)]"
                {...register(s.field)}
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium text-foreground">{t(`appConfig.guest.${s.field}`)}</span>
                <span className="text-xs text-[var(--g-secondary)]">
                  {missing
                    ? t("appConfig.guest.flagMissing", { key: s.key })
                    : t(`appConfig.guest.${s.field}Hint`)}
                </span>
              </span>
            </label>
          );
        })}
        {!canManageFlags ? (
          <p className="text-xs text-[var(--g-secondary)]">{t("appConfig.guest.flagsReadOnly")}</p>
        ) : null}
      </fieldset>

      <fieldset className="grid gap-4 md:grid-cols-2" disabled={!canWriteConfig}>
        <legend className="mb-1 text-sm font-semibold text-foreground md:col-span-2">
          {t("appConfig.guest.limits")}
        </legend>
        {GUEST_NUMBERS.map((n) => (
          <Field
            key={n.key}
            label={t(`appConfig.guest.${n.field}`)}
            inputMode="numeric"
            data-testid={n.testId}
            hint={t(`appConfig.guest.${n.field}Hint`, { min: n.min, max: n.max })}
            error={
              errors[n.field] !== undefined
                ? t("appConfig.guest.rangeError", { min: n.min, max: n.max })
                : undefined
            }
            {...register(n.field)}
          />
        ))}
        {!canWriteConfig ? (
          <p className="text-xs text-[var(--g-secondary)] md:col-span-2">{t("appConfig.readOnlyNote")}</p>
        ) : null}
      </fieldset>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          data-testid="guest-access-save"
          loading={isSaving}
          disabled={!canManageFlags && !canWriteConfig}
        >
          {t("appConfig.guest.save")}
        </Button>
        <p
          role="status"
          aria-live="polite"
          data-testid="guest-access-status"
          data-state={status.state}
          className={
            status.state === "error"
              ? "text-sm text-[var(--g-error)]"
              : "text-sm text-[var(--g-secondary)]"
          }
        >
          {statusText}
        </p>
      </div>
    </form>
  );
}

export default GuestChatConfigForm;
